import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { LineChart } from 'app/shared/charts/line';
import { PillToggle } from 'app/shared/pill-toggle';
import { DateRangePicker } from 'app/shared/datepickers';
import { Icon } from 'assets/icons';
import { useMailingReportService } from 'hooks/mailing/report';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useAIReportService } from 'hooks/ai/report';
import { processDateRange } from 'app/shared/utils/chart';
import explainStyles from './explain.module.css';
import styles from './campaign.module.css';

// ----- Constants -----

const RANGE_LS_KEY = (org_id) => `gomailer_perf_range_${org_id}`;
const TOGGLE_LS_KEY = (org_id) => `gomailer_perf_toggles_${org_id}`;
const CUSTOM_LS_KEY = (org_id) => `gomailer_perf_custom_${org_id}`;

const METRIC_DEFS = [
  { key: 'sends', label: 'Sends', color: 'var(--blue-primary)', dashed: false, default_on: true },
  { key: 'opens', label: 'Opens', color: 'var(--green-primary)', dashed: false, default_on: true },
  { key: 'clicks', label: 'Clicks', color: 'var(--purple)', dashed: true, default_on: true },
  {
    key: 'bounces',
    label: 'Bounces',
    color: 'var(--orange-dark)',
    dashed: false,
    default_on: false
  }
];

const RANGE_OPTIONS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'custom', label: 'Custom' }
];

const EXPLAIN_STATE = {
  DEFAULT: 'DEFAULT',
  LOADING: 'LOADING',
  EXPLAINED: 'EXPLAINED',
  STALE: 'STALE',
  ERROR: 'ERROR'
};

const EXPLAIN_TIMEOUT_MS = 10000;

// ----- Helpers -----

function rangeToDateStrings(range) {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  return [from.toISOString().slice(0, 10), to.toISOString().slice(0, 10)];
}

function parseToggles(raw, default_state) {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return { ...default_state, ...parsed };
  } catch {}
  return default_state;
}

function rangeLabel(range, custom_dates) {
  if (range === '7d') return '7 days';
  if (range === '30d') return '30 days';
  if (range === '90d') return '90 days';
  if (custom_dates && custom_dates.length === 2) {
    const fmt = (s) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(custom_dates[0])} – ${fmt(custom_dates[1])}`;
  }
  return 'selected range';
}

function rangeDateLabel(range, custom_dates) {
  if (range === '7d') return 'Last 7 days';
  if (range === '30d') return 'Last 30 days';
  if (range === '90d') return 'Last 90 days';
  if (custom_dates && custom_dates.length === 2) {
    const fmt = (s) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(custom_dates[0])} – ${fmt(custom_dates[1])}`;
  }
  return 'Custom range';
}

function trend(first_half, second_half, key) {
  const avg = (arr) => arr.reduce((s, d) => s + (d[key] || 0), 0) / (arr.length || 1);
  const a = avg(first_half);
  const b = avg(second_half);
  if (b > a * 1.05) return 'up';
  if (b < a * 0.95) return 'down';
  return 'flat';
}

function buildContext({
  chart_data,
  campaigns_raw,
  toggles,
  range,
  custom_dates,
  from_str,
  to_str
}) {
  const active_metrics = METRIC_DEFS.filter((m) => toggles[m.key]).map((m) => m.key);
  const total_sends = chart_data.reduce((s, d) => s + (d.sends || 0), 0);
  const total_opens = chart_data.reduce((s, d) => s + (d.opens || 0), 0);
  const total_clicks = chart_data.reduce((s, d) => s + (d.clicks || 0), 0);
  const avg_open_rate = total_sends > 0 ? Math.round((total_opens / total_sends) * 100) : 0;
  const avg_ctor = total_opens > 0 ? Math.round((total_clicks / total_opens) * 100) : 0;

  const sorted = [...chart_data].sort((a, b) => (b.opens || 0) - (a.opens || 0));
  const peak = sorted[0];
  const lowest = sorted[sorted.length - 1];

  const mid = Math.floor(chart_data.length / 2);
  const first_half = chart_data.slice(0, mid);
  const second_half = chart_data.slice(mid);
  const open_rate_trend = trend(first_half, second_half, 'opens');
  const click_rate_trend = toggles.clicks ? trend(first_half, second_half, 'clicks') : null;

  return {
    date_range: { from: from_str, to: to_str, label: rangeDateLabel(range, custom_dates) },
    active_metrics,
    summary: {
      total_sends,
      total_opens,
      avg_open_rate,
      avg_ctor,
      peak_day: { date: peak?.day || null, opens: peak?.opens || 0 },
      lowest_day: { date: lowest?.day || null, opens: lowest?.opens || 0 },
      open_rate_trend,
      click_rate_trend
    },
    campaigns: campaigns_raw.map((c) => {
      const sent = c.stats?.sent || 0;
      const opened = c.stats?.opened || 0;
      const clicked = c.stats?.clicked_link || 0;
      const ts =
        typeof c.schedule?.date === 'number' ? c.schedule.date : Date.parse(c.schedule?.date);
      return {
        name: c.name,
        sent_at: ts ? new Date(ts).toISOString().slice(0, 10) : null,
        sent_count: sent,
        open_rate: sent > 0 ? Math.round((opened / sent) * 100) : 0,
        ctor: opened > 0 ? Math.round((clicked / opened) * 100) : 0
      };
    })
  };
}

// ----- Sub-components -----

const MarkerCard = ({ campaign }) => {
  const sent = campaign.stats?.sent || 0;
  const opened = campaign.stats?.opened || 0;
  const clicked = campaign.stats?.clicked_link || 0;
  const open_rate = sent > 0 ? ((opened / sent) * 100).toFixed(1) : '—';
  const ctor = opened > 0 ? ((clicked / opened) * 100).toFixed(1) : '—';
  return (
    <div className={styles.markerCard}>
      <p className={styles.markerName}>{campaign.name}</p>
      <div className={styles.markerStats}>
        <span>{sent.toLocaleString()} sent</span>
        <span>
          {open_rate}% open rate · {ctor}% CTOR
        </span>
      </div>
    </div>
  );
};

const ExplainPulseDots = () => (
  <span className={explainStyles.pulseDots}>
    <span className={explainStyles.pulseDot} />
    <span className={explainStyles.pulseDot} />
    <span className={explainStyles.pulseDot} />
  </span>
);

const ExplainFooter = ({ state, onClose, onRefresh, explanation, current_range_label }) => {
  const AIIcon = (
    <Icon
      name="ai"
      width={18}
      height={18}
      color="#185FA5"
      style={{ marginRight: 4, verticalAlign: 'middle' }}
    />
  );
  const Refresh = (
    <Icon
      name="refresh"
      width={15}
      height={15}
      style={{ marginRight: 4, verticalAlign: 'middle' }}
    />
  );
  const Close = <Icon name="close" width={18} height={18} style={{ verticalAlign: 'middle' }} />;

  if (state === EXPLAIN_STATE.LOADING) {
    return (
      <div className={explainStyles.explainLoading}>
        <div className={explainStyles.explainLoadingLabel}>{AIIcon} Reading your chart</div>
        <div className={explainStyles.explainLoadingBody}>
          <ExplainPulseDots /> Analysing your last {current_range_label}...
        </div>
      </div>
    );
  }

  if (state === EXPLAIN_STATE.ERROR) {
    return (
      <div className={explainStyles.explainLoading}>
        <div className={explainStyles.explainLoadingBody}>
          Couldn&apos;t generate a summary right now &mdash;{' '}
          <button className={explainStyles.explainRefresh} onClick={onRefresh} type="button">
            try again
          </button>
        </div>
      </div>
    );
  }

  if (state === EXPLAIN_STATE.EXPLAINED || state === EXPLAIN_STATE.STALE) {
    return (
      <div className={explainStyles.explainPanel}>
        <div className={explainStyles.explainHeader}>
          <span className={explainStyles.explainTitle}>{AIIcon} Chart summary</span>
          <button
            className={explainStyles.explainClose}
            onClick={onClose}
            type="button"
            title="Close explanation"
            aria-label="Close explanation"
          >
            {Close}
          </button>
        </div>
        <div
          className={
            explainStyles.explainBody +
            (state === EXPLAIN_STATE.STALE ? ' ' + explainStyles.explainBodyFaded : '')
          }
        >
          {explanation}
        </div>
        {state === EXPLAIN_STATE.STALE && (
          <div className={explainStyles.explainStalePrompt}>
            {Refresh}↺ You changed the view — want a fresh read?
            <button className={explainStyles.explainRefresh} onClick={onRefresh} type="button">
              Refresh
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ----- Main Component -----

export const CampaignPerformanceChart = () => {
  const { tenant_id } = useSelector((store) => store.user_data);
  const { fetchReports } = useMailingReportService();
  const { fetchCampaigns } = useCampaignService();
  const { fetchChartExplain } = useAIReportService();

  const default_toggles = METRIC_DEFS.reduce((acc, m) => ({ ...acc, [m.key]: m.default_on }), {});

  const [range, setRange] = useState(() => {
    const stored = localStorage.getItem(RANGE_LS_KEY(tenant_id));
    return RANGE_OPTIONS.some((o) => o.value === stored) ? stored : '30d';
  });
  const [custom_dates, setCustomDates] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CUSTOM_LS_KEY(tenant_id))) || null;
    } catch {
      return null;
    }
  });
  const [toggles, setToggles] = useState(() =>
    parseToggles(localStorage.getItem(TOGGLE_LS_KEY(tenant_id)), default_toggles)
  );
  const [chart_data, setChartData] = useState([]);
  const [campaigns_raw, setCampaignsRaw] = useState([]);
  const [markers, setMarkers] = useState({});
  const [loading, setLoading] = useState(true);
  const [active_dates, setActiveDates] = useState(null);

  const [explain_state, setExplainState] = useState(EXPLAIN_STATE.DEFAULT);
  const [explanation, setExplanation] = useState('');
  const explain_loading = explain_state === EXPLAIN_STATE.LOADING;

  // Track whether we're mid-explain so stale triggers work correctly
  const explain_active = useRef(false);
  explain_active.current =
    explain_state === EXPLAIN_STATE.EXPLAINED || explain_state === EXPLAIN_STATE.STALE;

  const load = useCallback(
    async (dates) => {
      if (!dates || dates.length < 2) return;
      const [from, to] = dates;
      setLoading(true);
      try {
        const ranges = processDateRange(from, to);
        const min_ts = ranges[0].start;
        const max_ts = ranges[ranges.length - 1].end;

        const [{ logs = [] }, { campaigns = [] }] = await Promise.all([
          fetchReports({
            query_string: `tenant_id=${tenant_id}&resource_type=campaign&day=${min_ts}~${max_ts}`
          }),
          fetchCampaigns({
            query_string: `status=processed&tenant_id=${tenant_id}&schedule.date=${from}~${to}`
          })
        ]);

        // Aggregate logs into range buckets
        const by_label = {};
        for (const log of logs) {
          const bucket = ranges.find((r) => log.day >= r.start && log.day <= r.end);
          if (!bucket) continue;
          if (!by_label[bucket.label])
            by_label[bucket.label] = { sends: 0, opens: 0, clicks: 0, bounces: 0 };
          by_label[bucket.label].sends += log.stats?.sent || 0;
          by_label[bucket.label].opens += log.stats?.opened || 0;
          by_label[bucket.label].clicks += log.stats?.clicked_link || 0;
          by_label[bucket.label].bounces += log.stats?.bounced || 0;
        }

        const data = ranges.map(({ label }) => ({
          day: label,
          sends: 0,
          opens: 0,
          clicks: 0,
          bounces: 0,
          ...(by_label[label] || {})
        }));

        // Group campaigns into range buckets for marker nodes
        const marker_map = {};
        for (const campaign of campaigns) {
          const ts =
            typeof campaign.schedule?.date === 'number'
              ? campaign.schedule.date
              : Date.parse(campaign.schedule?.date);
          if (!ts || isNaN(ts)) continue;
          const bucket = ranges.find((r) => ts >= r.start && ts <= r.end);
          if (!bucket) continue;
          if (!marker_map[bucket.label]) marker_map[bucket.label] = [];
          marker_map[bucket.label].push(campaign);
        }

        const marker_nodes = Object.fromEntries(
          Object.entries(marker_map).map(([label, camps]) => [
            label,
            <>
              {camps.map((c) => (
                <MarkerCard key={c.id} campaign={c} />
              ))}
            </>
          ])
        );

        setChartData(data);
        setCampaignsRaw(campaigns);
        setMarkers(marker_nodes);
      } catch {
        setChartData([]);
        setCampaignsRaw([]);
        setMarkers({});
      } finally {
        setLoading(false);
      }
    },
    [tenant_id]
  );

  // Initial load and page-focus refresh
  useEffect(() => {
    const dates = range === 'custom' ? custom_dates : rangeToDateStrings(range);
    if (!dates) return;
    setActiveDates(dates);
    load(dates);

    const handleFocus = () => {
      if (!document.hidden) load(dates);
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [load, range, custom_dates]);

  // Mark explanation stale when range or toggles change while one is showing
  const is_first_render = useRef(true);
  useEffect(() => {
    if (is_first_render.current) {
      is_first_render.current = false;
      return;
    }
    if (explain_active.current) setExplainState(EXPLAIN_STATE.STALE);
  }, [range, custom_dates, toggles]);

  const handleRangeChange = (value) => {
    setRange(value);
    localStorage.setItem(RANGE_LS_KEY(tenant_id), value);
  };

  const handleCustomDates = (val) => {
    if (!val || val.length < 2) return;
    setCustomDates(val);
    localStorage.setItem(CUSTOM_LS_KEY(tenant_id), JSON.stringify(val));
  };

  const handleToggle = (key) => {
    const next = { ...toggles, [key]: !toggles[key] };
    setToggles(next);
    localStorage.setItem(TOGGLE_LS_KEY(tenant_id), JSON.stringify(next));
  };

  const runExplain = async () => {
    setExplainState(EXPLAIN_STATE.LOADING);
    try {
      const context = buildContext({
        chart_data,
        campaigns_raw,
        toggles,
        range,
        custom_dates,
        from_str: active_dates?.[0],
        to_str: active_dates?.[1]
      });

      let timer;
      const timeout = new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), EXPLAIN_TIMEOUT_MS);
      });

      const result = await Promise.race([fetchChartExplain(context), timeout]);
      clearTimeout(timer);

      if (!result) throw new Error('empty');
      setExplanation(result);
      setExplainState(EXPLAIN_STATE.EXPLAINED);
    } catch {
      setExplainState(EXPLAIN_STATE.ERROR);
    }
  };

  const handleExplainClose = () => {
    setExplainState(EXPLAIN_STATE.DEFAULT);
    setExplanation('');
  };

  const active_lines = METRIC_DEFS.filter((m) => toggles[m.key]).map((m) => ({
    key: m.key,
    label: m.label,
    color: m.color,
    dashed: m.dashed
  }));

  const total_sends = chart_data.reduce((sum, d) => sum + (d.sends || 0), 0);
  const is_empty = !loading && total_sends === 0;
  const current_range_label = rangeLabel(range, custom_dates);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Email Performance</span>
        <PillToggle options={RANGE_OPTIONS} value={range} onChange={handleRangeChange} />
      </div>

      {range === 'custom' && (
        <div className={styles.customRange}>
          <DateRangePicker
            className={styles.emailChartDatePicker}
            initial_value={custom_dates || undefined}
            onChange={handleCustomDates}
          />
        </div>
      )}

      <div className={styles.chartArea}>
        {loading ? (
          <Skeleton width="100%" height="100%" />
        ) : is_empty ? (
          <div className={styles.empty}>
            <span className={styles.emptyTitle}>
              {range === '30d' && !custom_dates
                ? 'Your email performance will appear here after your first send.'
                : 'No campaigns sent in this period.'}
            </span>
          </div>
        ) : (
          <LineChart data={chart_data} lines={active_lines} xKey="day" markers={markers} />
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.legend}>
          {METRIC_DEFS.map((m) => (
            <button
              key={m.key}
              className={`${styles.legendItem}${toggles[m.key] ? '' : ` ${styles.legendItemOff}`}`}
              onClick={() => handleToggle(m.key)}
              type="button"
              aria-pressed={toggles[m.key]}
              aria-label={`${m.label} — ${toggles[m.key] ? 'visible' : 'hidden'}`}
            >
              {m.dashed ? (
                <span className={styles.legendDash} style={{ backgroundColor: m.color }} />
              ) : (
                <span className={styles.legendDot} style={{ backgroundColor: m.color }} />
              )}
              <span className={styles.legendLabel}>{m.label}</span>
            </button>
          ))}
        </div>
        <button
          className={explainStyles.explainBtn}
          onClick={runExplain}
          disabled={explain_loading}
          type="button"
        >
          <Icon name="ai" width={18} height={18} color="#185FA5" />
          Explain this chart
        </button>
      </div>

      {explain_state !== EXPLAIN_STATE.DEFAULT && (
        <ExplainFooter
          state={explain_state}
          onClose={handleExplainClose}
          onRefresh={runExplain}
          explanation={explanation}
          current_range_label={current_range_label}
        />
      )}
    </div>
  );
};

export { ExplainFooter };
